import { useEffect, useState } from 'react'
import { useLocation, useSearch } from 'wouter'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { Spinner } from '@/components/Spinner'
import { Filters } from '@/components/Filters'
import { LinkCard } from '@/components/LinkCard'
import { NoResults } from '@/components/NoResults'
import { ShareLink } from '@/components/ShareLink'
import { useDeleteLink } from '@/hooks/useDeleteLink'
import { useLinks } from '@/hooks/useLinks'
import { useToast } from '@/hooks/Toast'
import type { Link, LinkTypeParam } from '@/types'

import s from './Links.module.css'

export const Links = () => {
  const [, setLocation] = useLocation()
  const search = useSearch()

  const params = new URLSearchParams(search)
  const query = params.get('q')
  const type = params.get('type') as LinkTypeParam

  const {
    data: links,
    totalCount,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    loadMoreRef,
  } = useLinks({
    type,
    query,
  })

  const [deleteLinkId, setDeleteLinkId] = useState('')
  const [sharingLink, setSharingLink] = useState<Link>()
  const [linkFromStorage, setLinkFromStorage] = useState<Link | null>(null)
  const deleteLinkMutation = useDeleteLink()
  const toast = useToast()

  const handleEdit = (link: Link) => {
    // Store link data in localStorage for the edit page since wouter doesn't support state
    localStorage.setItem('editingLink', JSON.stringify(link))
    setLocation(`/edit/${link.id}`)
  }

  const handleDeleteLink = async () => {
    try {
      await deleteLinkMutation.mutateAsync({
        linkId: deleteLinkId,
      })
      setDeleteLinkId('')
    } catch (error) {
      toast.error('Something went wrong. Try again?')
    }
  }

  useEffect(() => {
    const storedLink = localStorage.getItem('newLink')
    if (storedLink) {
      setLinkFromStorage(JSON.parse(storedLink))
      localStorage.removeItem('newLink')
    }
  }, [])

  useEffect(() => {
    if (!isFetching) {
      setLinkFromStorage(null)
    }
  }, [isFetching])

  if (links.length === 0 && !type && !query && !isFetching) {
    return <EmptyState />
  }

  return (
    <div className={s.links}>
      <Filters totalCount={totalCount} isFetching={isFetching && !!type} />
      {isLoading ? (
        <Spinner />
      ) : links.length ? (
        <ErrorBoundary>
          {linkFromStorage && isFetching && (
            <div className={s.expand}>
              <LinkCard
                link={linkFromStorage}
                onEdit={() => handleEdit(linkFromStorage)}
                onShare={() => setSharingLink(linkFromStorage)}
                onDelete={() => setDeleteLinkId(linkFromStorage.id)}
              />
            </div>
          )}
          {links.map((link) => (
            <LinkCard
              key={link.id}
              link={link}
              onEdit={() => handleEdit(link)}
              onShare={() => setSharingLink(link)}
              onDelete={() => setDeleteLinkId(link.id)}
            />
          ))}
          {hasNextPage && (
            <div ref={loadMoreRef} className={s.loadMore}>
              {isFetchingNextPage && <Spinner inline />}
            </div>
          )}
        </ErrorBoundary>
      ) : linkFromStorage && isFetching ? (
        <LinkCard
          link={linkFromStorage}
          onEdit={() => handleEdit(linkFromStorage)}
          onShare={() => setSharingLink(linkFromStorage)}
          onDelete={() => setDeleteLinkId(linkFromStorage.id)}
        />
      ) : (
        <NoResults query={query} type={type} />
      )}
      <Modal
        title="Delete Link"
        description="Are you sure you want to delete the link?"
        open={!!deleteLinkId}
        onClose={() => setDeleteLinkId('')}
      >
        <Button
          variant="destructive"
          disabled={deleteLinkMutation.isPending}
          onClick={handleDeleteLink}
        >
          Delete
        </Button>
      </Modal>
      <Modal
        title={`Sharing '/${sharingLink?.slug}'`}
        open={!!sharingLink}
        onClose={() => setSharingLink(undefined)}
      >
        <ShareLink link={sharingLink} />
      </Modal>
    </div>
  )
}
